// src/composables/useLanguage.js
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// Language configuration
export const SUPPORTED_LANGUAGES = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    flag: "usa",
    rtl: false
  },
  {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    flag: "es",
    rtl: false
  },
  {
    code: "fr",
    name: "French",
    nativeName: "Français",
    flag: "fr",
    rtl: false
  },
  {
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    flag: "de",
    rtl: false
  },
  {
    code: "ar",
    name: "Arabic",
    nativeName: "العربية",
    flag: "ar",
    rtl: true
  },
  {
    code: "zh",
    name: "Chinese",
    nativeName: "中文",
    flag: "cn",
    rtl: false
  },
  {
    code: "zh-cn",
    name: "Mandarin",
    nativeName: "简体中文",
    flag: "cn",
    rtl: false
  },
  {
    code: "ja",
    name: "Japanese",
    nativeName: "日本語",
    flag: "jp",
    rtl: false
  },
  {
    code: "bn",
    name: "Bengali",
    nativeName: "বাংলা",
    flag: "bd",
    rtl: false
  },
  {
    code: "ko",
    name: "Korean",
    nativeName: "한국어",
    flag: "kr",
    rtl: false
  },
  {
    code: "ur",
    name: "Urdu",
    nativeName: "اردو",
    flag: "pk",
    rtl: true
  },
  {
    code: "wo",
    name: "Wolof",
    nativeName: "Wolof",
    flag: "sn",
    rtl: false
  },
  {
    code: "ru",
    name: "Russian",
    nativeName: "Русский",
    flag: "ru",
    rtl: false
  },
  {
    code: "uk",
    name: "Ukrainian",
    nativeName: "Українська",
    flag: "ua",
    rtl: false
  },
  {
    code: "uz",
    name: "Uzbek",
    nativeName: "Oʻzbek",
    flag: "uz",
    rtl: false
  },
  {
    code: "ka",
    name: "Georgian",
    nativeName: "ქართული",
    flag: "ge",
    rtl: false
  },
  {
    code: "yue",
    name: "Cantonese",
    nativeName: "粵語",
    flag: "hk",
    rtl: false
  },
  {
    code: "ht",
    name: "Haitian Creole",
    nativeName: "Kreyòl ayisyen",
    flag: "ht",
    rtl: false
  }
];

export const RTL_LANGUAGES = ["ar", "ur"];

export function useLanguage() {
  const i18n = useI18n();

  const currentLanguage = computed(() => {
    return (
      SUPPORTED_LANGUAGES.find((lang) => lang.code === i18n.locale.value) || SUPPORTED_LANGUAGES[0]
    );
  });

  const isRTL = computed(() => {
    return RTL_LANGUAGES.includes(i18n.locale.value);
  });

  function applyDirection(locale) {
    const htmlElement = document.documentElement;
    const isRTLLocale = RTL_LANGUAGES.includes(locale);

    if (isRTLLocale) {
      htmlElement.setAttribute("dir", "rtl");
      htmlElement.setAttribute("lang", locale);
      document.body.classList.add("rtl");
    } else {
      htmlElement.setAttribute("dir", "ltr");
      htmlElement.setAttribute("lang", locale);
      document.body.classList.remove("rtl");
    }
  }

  function setDayjsLocale(locale) {
    // Import dayjs dynamically to avoid bundle bloat
    import("dayjs").then(({ default: dayjs }) => {
      const localeMap = {
        en: "en",
        es: "es",
        fr: "fr",
        de: "de",
        ar: "ar",
        zh: "zh",
        ja: "ja",
        bn: "bn",
        ko: "ko",
        ur: "ur",
        wo: "en", // Wolof not available in dayjs, fallback to English
        ru: "ru",
        uk: "uk",
        "zh-cn": "zh-cn",
        uz: "uz",
        ka: "ka",
        yue: "zh-hk", // Cantonese - use Hong Kong locale
        ht: "ht"
      };
      dayjs.locale(localeMap[locale] || "en");
    });
  }

  function changeLocale(locale, shouldReload = true) {
    if (!SUPPORTED_LANGUAGES.find((lang) => lang.code === locale)) {
      console.warn(`Unsupported locale: ${locale}`);
      return;
    }

    i18n.locale.value = locale;
    localStorage.setItem("ptcwizard_locale", locale);

    // Apply RTL/LTR direction and dayjs locale
    applyDirection(locale);
    setDayjsLocale(locale);

    // Reload to ensure all components update properly
    if (shouldReload) {
      window.location.reload();
    }
  }

  function getLanguageByCode(code) {
    return SUPPORTED_LANGUAGES.find((lang) => lang.code === code);
  }

  function getAvailableLanguages() {
    return SUPPORTED_LANGUAGES;
  }

  function getLanguageName(code) {
    if (!code) return "Unknown";
    const language = SUPPORTED_LANGUAGES.find((lang) => lang.code === code);
    return language ? language.name : "Unknown";
  }

  return {
    currentLanguage,
    isRTL,
    supportedLanguages: SUPPORTED_LANGUAGES,
    rtlLanguages: RTL_LANGUAGES,
    applyDirection,
    setDayjsLocale,
    changeLocale,
    getLanguageByCode,
    getAvailableLanguages,
    getLanguageName
  };
}

// Utility function to get language direction
export function getLanguageDirection(locale) {
  return RTL_LANGUAGES.includes(locale) ? "rtl" : "ltr";
}

// Utility function to check if locale is RTL
export function isRTLLocale(locale) {
  return RTL_LANGUAGES.includes(locale);
}

// Utility function to get language name from code
export function getLanguageName(code) {
  if (!code) return "Unknown";
  const language = SUPPORTED_LANGUAGES.find((lang) => lang.code === code);
  return language ? language.name : "Unknown";
}
